import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent, createRef } from 'react';
import { Transition } from 'react-transition-group';
import { O as OuterWrapper, C as Content } from './PopoverLegacy.styles-D6Hh412w.js';
import { O as OnTargetMove } from './OnTargetMove-UrZt4VFG.js';
import { g as getElement } from './getElement-zsbq1uP4.js';
import { P as Portal } from '../Portal/Portal-CfuGnhf3.js';
import { P as PortalContextProvider } from '../Portal/PortalContext-CNkIMMvm.js';
import { P as PortalComponent } from '../Portal/PortalComponent-CW4tBMeI.js';
import { i as isFunction } from '../LocalModules/storm-ui-utils/dist/esm/isFunction-DjxTmSal-DjxTmSal.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';
import { i as isEventFromWithinElement } from '../LocalModules/storm-ui-utils/dist/esm/isEventFromWithinElement-BvBeBwSW-BvBeBwSW.js';
import { O as OnRootContainerMousedown } from '../LocalModules/storm-ui-utils/dist/esm/OnRootContainerMousedown-Bh14xj3_-BLKWZj31.js';
import { c as composeRefs } from '../LocalModules/storm-ui-utils/dist/esm/composeRefs-CHPW6wJ8-CHPW6wJ8.js';
import { p as pageDirection } from '../LocalModules/storm-ui-utils/dist/esm/pageDirection-BYrhGUoU-BYrhGUoU.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

// how close a popover can be to the edge before swapping sides
const SWAP_MARGIN = 2;
const findAbsoluteCenterY = rect => window.innerHeight / 2 - rect.height / 2;
const findAbsoluteCenterX = rect => window.innerWidth / 2 - rect.width / 2;
const trimOverflowTop = y => {
  if (y < SWAP_MARGIN) return SWAP_MARGIN;
  return y;
};
const trimOverflowLeft = x => {
  if (x < SWAP_MARGIN) return SWAP_MARGIN;
  return x;
};
const trimOverflowRight = (x, width) => {
  if (x + width > window.innerWidth - SWAP_MARGIN) {
    return window.innerWidth - SWAP_MARGIN - width;
  }
  return x;
};
const trimOverflowBottom = (y, height) => {
  if (y + height > window.innerHeight - SWAP_MARGIN) {
    return window.innerHeight - SWAP_MARGIN - height;
  }
  return y;
};

// eslint-disable-next-line max-len
const adjustLeftForY = (align, anchorRect, wrapperRect, offset) => {
  if (align === 'end') {
    // eslint-disable-next-line max-len
    return trimOverflowLeft(anchorRect.left + anchorRect.width - wrapperRect.width - Number(offset.x));
  }
  if (align === 'center') {
    const center = trimOverflowRight(anchorRect.left + anchorRect.width / 2 - (wrapperRect.width / 2 + Number(offset.x)), 0);
    return trimOverflowLeft(trimOverflowRight(center, 0));
  }
  return trimOverflowRight(anchorRect.left + Number(offset.x), wrapperRect.width);
};
// eslint-disable-next-line max-len
const adjustTopForX = (align, anchorRect, wrapperRect, offset) => {
  if (align === 'end') {
    // eslint-disable-next-line max-len
    return trimOverflowTop(anchorRect.top + anchorRect.height - wrapperRect.height - Number(offset.y));
  }
  if (align === 'center') {
    const center = trimOverflowTop(anchorRect.top + anchorRect.height / 2 - (wrapperRect.height / 2 + Number(offset.y)));
    return trimOverflowBottom(trimOverflowTop(center), wrapperRect.height);
  }
  return trimOverflowBottom(anchorRect.top + Number(offset.y), wrapperRect.height);
};
const calculateAbsoluteHorizontalPosition = position => {
  if (pageDirection() === 'rtl' && (position === 'right' || position === 'left')) {
    if (position === 'right') {
      return 'left';
    }
    return 'right';
  }
  return position;
};
const getRelativeOverlayAlign = align => {
  if (pageDirection() === 'rtl' && (align === 'start' || align === 'end')) {
    if (align === 'start') {
      return 'end';
    }
    return 'start';
  }
  return align;
};

// eslint-disable-next-line max-len
const getRelativeOverlay = (align, anchorRect, wrapperRect, offset) => {
  const top = trimOverflowBottom(anchorRect.top + Number(offset.y), wrapperRect.height) || 0;
  const alignBasedOnPageDirection = getRelativeOverlayAlign(align);
  let left;
  switch (alignBasedOnPageDirection) {
    case 'end':
      left = anchorRect.left + anchorRect.width - wrapperRect.width - Number(offset.x);
      break;
    case 'center':
      // eslint-disable-next-line max-len
      left = anchorRect.left + anchorRect.width / 2 - (wrapperRect.width / 2 + Number(offset.x));
      break;
    case 'start':
    default:
      left = anchorRect.left + Number(offset.x);
      break;
  }
  return {
    left: trimOverflowRight(left, wrapperRect.width) || 0,
    top
  };
};
class PopoverLegacy extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "wrapperRef", void 0);
    _defineProperty(this, "portalRef", void 0);
    _defineProperty(this, "teardownPopover", () => {
      const {
        popoverElementWillUnmount
      } = this.props;
      // eslint-disable-next-line max-len
      if (popoverElementWillUnmount && isFunction(popoverElementWillUnmount) && this.wrapperRef.current instanceof HTMLElement) {
        popoverElementWillUnmount(this.wrapperRef.current);
      }
    });
    _defineProperty(this, "handleEntered", () => {
      const {
        showFocus,
        popoverElementDidMount
      } = this.props;
      const focusableEls = getFocusableElements(this.wrapperRef.current);
      if (document.activeElement && document.activeElement instanceof HTMLElement && showFocus && focusableEls.length > 0 && focusableEls.indexOf(document.activeElement) < 0) {
        focusableEls[0].focus();
      }
      // eslint-disable-next-line max-len
      if (popoverElementDidMount && isFunction(popoverElementDidMount) && this.wrapperRef.current instanceof HTMLElement) {
        popoverElementDidMount(this.wrapperRef.current);
      }
    });
    _defineProperty(this, "handleExited", () => {
      const shouldShowFocus = this.props.showFocus && document.activeElement === document.body;
      if (shouldShowFocus && this.props.anchorEl) {
        const element = getElement(this.props.anchorEl);
        if (element && element instanceof HTMLElement) {
          const focusableEls = getFocusableElements(element);
          if (focusableEls.length > 0) {
            // e.g. tooltip trigger element has a span wrapper
            focusableEls[0].focus();
          } else {
            element.focus(); // simple trigger like button
          }
        }
      }
    });
    _defineProperty(this, "handleClick", event => {
      const {
        onClose
      } = this.props;
      if (this.wrapperRef.current && !isEventFromWithinElement(event, this.wrapperRef.current)) {
        onClose === null || onClose === void 0 || onClose(event);
      }
    });
    _defineProperty(this, "handleScroll", () => {
      const {
        anchorEl
      } = this.props;
      if (!getElement(anchorEl) || !this.wrapperRef.current) return;
      window.requestAnimationFrame(() => {
        /**
         * Check if wrapperRef still exists. There is a edge case when `requestAnimationFrame`
         * callback happens after wrapperRef is removed from the DOM
         */
        if (this.wrapperRef.current) {
          const newLocation = this.calculateRelativeLocation();
          if (this.wrapperRef.current instanceof HTMLElement) {
            this.wrapperRef.current.style.left = `${newLocation.left}px`;
            this.wrapperRef.current.style.top = `${newLocation.top}px`;
          }
        }
      });
    });
    // eslint-disable-next-line max-len
    _defineProperty(this, "calculateAbsoluteHorizontal", wrapperRect => {
      const {
        position = 'overlay',
        align = 'start'
      } = this.props;
      const offset = this.getValidOffset();
      const vertical = align === 'end' ? 'bottom' : 'top';
      const positionBasedOnPageDirection = calculateAbsoluteHorizontalPosition(position);
      return {
        position: 'fixed',
        [positionBasedOnPageDirection]: Number(offset.x),
        [vertical]: align === 'center' ? findAbsoluteCenterY(wrapperRect) : Number(offset.y)
      };
    });
    _defineProperty(this, "calculateAbsoluteVertical", wrapperRect => {
      const {
        position = 'overlay',
        align = 'start'
      } = this.props;
      const offset = this.getValidOffset();
      const horizontal = align === 'end' ? 'right' : 'left';
      return {
        position: 'fixed',
        [position]: Number(offset.y),
        [horizontal]: align === 'center' ? findAbsoluteCenterX(wrapperRect) : Number(offset.x)
      };
    });
    // eslint-disable-next-line max-len
    _defineProperty(this, "calculateRelativeVertical", (anchorRect, wrapperRect) => {
      const {
        position = 'overlay',
        align = 'start',
        onUpdatePosition,
        disableFlip
      } = this.props;
      const offset = this.getValidOffset();
      const above = anchorRect.top - wrapperRect.height - Number(offset.y);
      const below = anchorRect.top + anchorRect.height + Number(offset.y);
      let actualPosition = position;
      if (!disableFlip) {
        if (position === 'bottom' && below + wrapperRect.height > window.innerHeight - SWAP_MARGIN) {
          actualPosition = 'top';
        } else if (position === 'top' && above < SWAP_MARGIN) {
          actualPosition = 'bottom';
        }
      }
      actualPosition = onUpdatePosition ? onUpdatePosition(actualPosition) : actualPosition;
      // TODO: Figure out fallback for when position is no longer top or bottom

      return {
        top: (actualPosition === 'top' ? above : below) || 0,
        left: adjustLeftForY(align, anchorRect, wrapperRect, offset) || 0
      };
    });
    // eslint-disable-next-line max-len
    _defineProperty(this, "calculateRelativeHorizontal", (anchorRect, wrapperRect) => {
      const {
        position = 'overlay',
        align = 'start',
        onUpdatePosition,
        disableFlip
      } = this.props;
      const offset = this.getValidOffset();
      let left;
      let right;
      let actualPosition = position;
      if (pageDirection() !== 'rtl') {
        left = anchorRect.left - wrapperRect.width - Number(offset.x);
        right = anchorRect.left + anchorRect.width + Number(offset.x);
      } else {
        left = anchorRect.left + anchorRect.width + Number(offset.x);
        right = anchorRect.left - wrapperRect.width - Number(offset.x);
      }
      if (!disableFlip) {
        if (position === 'right' && right + wrapperRect.width > window.innerWidth - SWAP_MARGIN) {
          actualPosition = 'left';
        } else if (position === 'left' && left < SWAP_MARGIN) {
          actualPosition = 'right';
        }
      }
      actualPosition = onUpdatePosition ? onUpdatePosition(actualPosition) : actualPosition;
      // TODO: Figure out fallback for when position is no longer left or right

      return {
        left: (actualPosition === 'left' ? left : right) || 0,
        top: adjustTopForX(align, anchorRect, wrapperRect, offset) || 0
      };
    });
    _defineProperty(this, "preventClickThrough", event => {
      event.stopPropagation();
      if (this.props.onClick) {
        this.props.onClick(event);
      }
    });
    _defineProperty(this, "onWrapperMount", element => {
      const {
        attachCustomEvents
      } = this.props;
      if (element && attachCustomEvents) {
        attachCustomEvents(element);
      }
      /* recalculate the position as soon as it is mounted to the dom */
      this.handleScroll();
    });
    this.wrapperRef = /*#__PURE__*/createRef();
    this.portalRef = /*#__PURE__*/createRef();
  }
  componentWillUnmount() {
    const {
      detachCustomEvents
    } = this.props;
    if (detachCustomEvents) {
      detachCustomEvents();
    }
  }
  getValidOffset() {
    const {
      offset
    } = this.props;
    if (!offset) return {
      x: 0,
      y: 0
    };
    return {
      x: Number(offset.x) || 0,
      y: Number(offset.y) || 0
    };
  }
  calculateAbsoluteLocation() {
    const {
      position = 'overlay'
    } = this.props;
    const offset = this.getValidOffset();
    const fallback = {
      position: 'fixed',
      left: Number(offset.x),
      top: Number(offset.y)
    };
    if (!this.wrapperRef.current) return fallback;
    const wrapperRect = this.wrapperRef.current.getBoundingClientRect();
    switch (position) {
      case 'right':
      case 'left':
        return this.calculateAbsoluteHorizontal(wrapperRect);
      case 'top':
      case 'bottom':
        return this.calculateAbsoluteVertical(wrapperRect);
      case 'overlay':
      default:
        return fallback;
    }
  }
  calculateRelativeLocation() {
    const offset = this.getValidOffset();
    if (!this.wrapperRef.current) {
      return {
        left: Number(offset.x),
        top: Number(offset.y)
      };
    }
    const {
      position = 'overlay',
      anchorEl,
      align = 'start'
    } = this.props;
    const element = getElement(anchorEl);
    if (element instanceof Element) {
      const anchorRect = element.getBoundingClientRect();
      const wrapperRect = this.wrapperRef.current.getBoundingClientRect();
      switch (position) {
        case 'right':
        case 'left':
          return this.calculateRelativeHorizontal(anchorRect, wrapperRect);
        case 'top':
        case 'bottom':
          return this.calculateRelativeVertical(anchorRect, wrapperRect);
        case 'overlay':
        default:
          return getRelativeOverlay(align, anchorRect, wrapperRect, offset);
      }
    }
    return {
      top: 0,
      left: 0
    };
  }
  wrapInPortalContext(content) {
    const {
      providePortal
    } = this.props;
    /* If `providePortal===false` then do not wrap the content in the PortalContextProvider */
    if (!providePortal) {
      return content;
    }
    return /*#__PURE__*/React__default.createElement(PortalContextProvider, {
      portalElementRef: this.portalRef
    }, content, /*#__PURE__*/React__default.createElement(PortalComponent, {
      ref: this.portalRef
    }));
  }
  renderContents(state) {
    const {
      children,
      contentClassName,
      anchorEl,
      position,
      transitionDuration,
      transition,
      style,
      withBackdrop,
      onUpdatePosition,
      onClose,
      offset,
      disableContentScroll,
      shadowRoot,
      providePortal,
      rootContainerRef,
      ...rest
    } = this.props;
    const location = getElement(anchorEl) ? this.calculateRelativeLocation() : this.calculateAbsoluteLocation();
    const wrapperStyles = {
      ...style,
      // to avoid user passed styles breaking locations etc
      ...location
    };
    return /*#__PURE__*/React__default.createElement(OnTargetMove, {
      targetEl: anchorEl,
      handler: this.handleScroll
    }, /*#__PURE__*/React__default.createElement(OnRootContainerMousedown, {
      rootContainer: shadowRoot !== null && shadowRoot !== void 0 ? shadowRoot : document.documentElement,
      handler: this.handleClick
    }, /*#__PURE__*/React__default.createElement(OuterWrapper, _extends({
      transitionState: state,
      transition: transition,
      style: wrapperStyles,
      withBackdrop: withBackdrop,
      ref: composeRefs(this.wrapperRef, this.onWrapperMount, rootContainerRef)
    }, rest, {
      onClick: this.preventClickThrough,
      $hideOverflow:
      /**
       * Hide the overflow When the popover is *not* providing a portal. The
       * overflow rules will be shifted to the Content component
       */
      !providePortal
    }), this.wrapInPortalContext(/*#__PURE__*/React__default.createElement(Content, {
      className: contentClassName,
      $disableContentScroll: disableContentScroll,
      $hideOverflow: providePortal
    }, children)))));
  }
  render() {
    const {
      transitionDuration = 250,
      isOpen,
      portalRef
    } = this.props;
    return /*#__PURE__*/React__default.createElement(Transition, {
      timeout: transitionDuration,
      appear: true,
      in: isOpen,
      onExit: this.teardownPopover,
      onEntered: this.handleEntered,
      onExited: this.handleExited,
      nodeRef: this.wrapperRef
    }, state => {
      if (state === 'exited') return null;
      return /*#__PURE__*/React__default.createElement(Portal, {
        externalPortalRef: portalRef,
        transitionDuration: transitionDuration
      }, this.renderContents(state));
    });
  }
}
_defineProperty(PopoverLegacy, "defaultProps", {
  onClick: undefined,
  withBackdrop: false,
  onClose: noop,
  contentClassName: '',
  anchorEl: undefined,
  position: 'overlay',
  align: 'start',
  offset: {
    x: 0,
    y: 0
  },
  transitionDuration: 250,
  transition: 'opacity 250ms ease-in-out',
  onUpdatePosition: position => position,
  style: {},
  showFocus: false,
  disableFlip: false,
  attachCustomEvents: undefined,
  detachCustomEvents: undefined,
  popoverElementDidMount: undefined,
  popoverElementWillUnmount: undefined,
  portalRef: undefined,
  shadowRoot: undefined,
  disableContentScroll: false,
  providePortal: undefined,
  rootContainerRef: undefined
});

export { PopoverLegacy as P };
//# sourceMappingURL=PopoverLegacy-Boas-h84.js.map
